import { computed, Injectable, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { CallRefinementServerStatus } from 'src/app/utils/enums/self-serve/call-refinement-server-status.enum';
import { CallRefinementStatus } from 'src/app/utils/enums/self-serve/call-refinement-status.enum';
import { SelfServeLocalStorageUtil } from 'src/app/utils/local-storage.util';
import { CallRefinementLocalStorageUtil } from 'src/app/utils/call-refinement-local-storage.util';
import {
  isTerminalCallRefinementServerStatus,
  mapServerStatusToCallRefinementStatus,
} from 'src/app/utils/call-refinement-status.util';
import { ICallRefinement } from 'src/app/interfaces/self-serve/icall-refinement';
import { AuthorService } from './author.service';
import { SseService } from 'src/app/services/sse.service';

const INITIAL_POLL_DELAY_MS = 2_000;
const POLL_INTERVAL_MS = 5_000;
const STALE_BACKOFF_INTERVAL_MS = 10_000;
const STALE_THRESHOLD_MS = 3 * 60_000;
const POLLING_CAP_MS = 20 * 60_000;
// While an SSE stream is healthy, polls only act as a safety net.
const SSE_SAFETY_INTERVAL_MS = 60_000;
const SSE_CONTEXT_TYPE = 'simulation_generation';

/** Polls the same job-status endpoint used for simulation generation
 * (SimulationGenerationPollingService), with the same state treatment: a
 * refinement request keeps running — and keeps polling — independently of
 * the generated-call-details view that started it. Quitting that view,
 * navigating back to the dashboard, and starting a refinement on a
 * *different* simulation must not interrupt the first one, and reloading
 * the page must resume any refinement still in flight. Hence localStorage
 * persistence + an init() hook, exactly like the generation service.
 *
 * Unlike that service's `generations` computed (which re-parses the whole
 * localStorage blob on every read), this keeps an in-memory Map as the
 * live source of truth and only touches localStorage on actual mutations
 * (poll responses), so reading getStatus() from a template stays O(1).
 *
 * SSE mirrors the generation service's hybrid approach: once a generationUuid
 * is available, the service connects to the simulation_generation stream and
 * listens for generation_status_changed events. Polling continues as a 60 s
 * safety net while SSE is live, and resumes at 5 s on stream close. */
@Injectable({
  providedIn: 'root',
})
export class CallRefinementPollingService {
  private readonly tick = signal(0);
  private readonly activeTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly records = new Map<string, ICallRefinement>();
  private readonly sseSubscriptions = new Map<string, Subscription>();
  private readonly sseGivenUp = new Set<string>();
  private readonly sseGapPollNeeded = new Set<string>();
  private hydrated = false;

  constructor(private authorService: AuthorService, private sseService: SseService) {}

  /** Call once, at app bootstrap — resumes polling for every still-
   * unresolved refinement left over from a previous session/navigation. A
   * terminal (Updated/Error) record means polling was NOT in flight when
   * the app last closed, so it's dropped here rather than hydrated: without
   * this, a finished refinement's status would resurface as if new on
   * every future reload, since a hard reload never fires the in-app
   * close/destroy cleanup that normally clears it (see dismiss()). */
  init(): void {
    if (this.hydrated) return;
    this.hydrated = true;

    const persisted = CallRefinementLocalStorageUtil.getAll();
    let droppedStale = false;

    persisted.forEach((record) => {
      if (isTerminalCallRefinementServerStatus(record.serverStatus)) {
        droppedStale = true;
        return;
      }

      this.records.set(record.callUuid, record);
      this.startPollingFor(record.callUuid);
    });

    if (droppedStale) this.persist();
    this.bumpTick();
  }

  /** Reactive read — call from within a computed()/effect() so it re-runs
   * whenever any tracked refinement's status changes. */
  getStatus(callUuid: string): CallRefinementStatus {
    this.tick();
    const record = this.records.get(callUuid);
    return mapServerStatusToCallRefinementStatus(record?.serverStatus, record?.reason);
  }

  /** Reactive read — true while a refinement for this call is still running
   * (requested or mid-processing), false once it completes or fails. */
  isRefining(callUuid: string): boolean {
    return this.getStatus(callUuid) === CallRefinementStatus.Thinking;
  }

  /** Terminal (Updated/Error/OutOfScope) refinements for the global banner
   * (CallRefinementBannerHostComponent). Scoring refinements are excluded —
   * they run automatically in the background and should not interrupt the
   * author with outcome toasts. */
  readonly terminalRefinements = computed(() => {
    this.tick();
    return Array.from(this.records.values()).filter((record) =>
      isTerminalCallRefinementServerStatus(record.serverStatus) && !record.isScoring,
    );
  });

  /** Call once the trigger-refinement API call has succeeded. Safe to call
   * again for a callUuid whose originating view was navigated away from and
   * back to — polling for a given callUuid never runs more than one chain
   * at a time.
   *
   * Pass `generationUuid` when it is already known (sim-generation modal or
   * getSimulationOverview response) to skip the initial status poll and
   * connect SSE immediately. Falls back to the poll-first flow if the UUID
   * is absent or if the SSE client cap is reached. */
  start(callUuid: string, callName?: string, moduleUuid?: string, simulationStatus?: string, isScoring?: boolean, generationUuid?: string | null): void {
    if (this.activeTimers.has(callUuid)) return;

    const now = Date.now();
    this.upsertRecord({
      callUuid,
      serverStatus: CallRefinementServerStatus.Requested,
      startedAt: now,
      statusChangedAt: now,
      callName,
      moduleUuid,
      simulationStatus,
      isScoring,
      ...(generationUuid ? { generationUuid } : {}),
    });

    if (generationUuid) {
      // Connect SSE immediately — no status poll needed to discover the UUID.
      this.connectSse(callUuid, generationUuid);

      // If the SSE client cap was hit, fall back to the poll-first flow so
      // the service discovers status through polling instead.
      const nextDelayMs = this.sseGivenUp.has(callUuid)
        ? INITIAL_POLL_DELAY_MS
        : SSE_SAFETY_INTERVAL_MS;

      const handle = setTimeout(() => {
        this.activeTimers.delete(callUuid);
        this.startPollingFor(callUuid);
      }, nextDelayMs);
      this.activeTimers.set(callUuid, handle);
      return;
    }

    // Wait before the first poll — the backend needs a moment to begin processing
    // the refinement job; polling immediately almost always returns the previous status.
    const handle = setTimeout(() => {
      this.activeTimers.delete(callUuid);
      this.startPollingFor(callUuid);
    }, INITIAL_POLL_DELAY_MS);
    this.activeTimers.set(callUuid, handle);
  }

  /** Call when the trigger-refinement API call itself fails — i.e. the
   * refinement never reached the backend job in the first place, so there's
   * nothing to poll for. Records an immediate Error result (no polling
   * started) so the UI unlocks and shows the failure the same way a polled
   * failure would, instead of staying disabled with no path back. */
  fail(callUuid: string, callName?: string, moduleUuid?: string, simulationStatus?: string): void {
    if (this.activeTimers.has(callUuid)) return; // an active chain already owns this record

    const now = Date.now();
    this.upsertRecord({
      callUuid,
      serverStatus: CallRefinementServerStatus.Failed,
      startedAt: now,
      statusChangedAt: now,
      simulationStatus,
      callName,
      moduleUuid,
    });
  }

  /** Drops all state for this call. Use once its outcome (Updated/Error) has
   * been shown to and acknowledged by the user — NOT merely navigated away
   * from, since navigating away must not stop polling (see class doc). */
  dismiss(callUuid: string): void {
    const handle = this.activeTimers.get(callUuid);
    if (handle) clearTimeout(handle);
    this.activeTimers.delete(callUuid);
    this.disconnectSse(callUuid);
    this.sseGivenUp.delete(callUuid);
    this.sseGapPollNeeded.delete(callUuid);
    this.records.delete(callUuid);
    this.persist();
    this.bumpTick();
  }

  private upsertRecord(record: ICallRefinement): void {
    this.records.set(record.callUuid, record);
    this.persist();
    this.bumpTick();
  }

  private persist(): void {
    CallRefinementLocalStorageUtil.saveAll(Array.from(this.records.values()));
  }

  private bumpTick(): void {
    this.tick.update((n) => n + 1);
  }

  private startPollingFor(callUuid: string): void {
    if (this.activeTimers.has(callUuid)) return; // already has an active chain — don't start a second one
    this.pollOne(callUuid);
  }

  private pollOne(callUuid: string): void {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    console.log(`[refinement-sse] poll → callUuid=${callUuid}`);

    this.authorService.getSimulationGenerationStatus(callUuid, accountUuid).subscribe({
      next: (res) => {
        const record = this.records.get(callUuid);
        if (!record) return; // dismissed mid-flight

        console.log(
          `[refinement-sse] poll response: callUuid=${callUuid} status=${res.status}` +
          (res.generationUuid ? ` generationUuid=${res.generationUuid}` : ' generationUuid=none'),
        );

        // Do not let an older poll roll back a newer terminal SSE update —
        // checked before the SSE connect block so a stale poll cannot reopen
        // the stream after SSE has already delivered a terminal event.
        if (isTerminalCallRefinementServerStatus(record.serverStatus) &&
            !isTerminalCallRefinementServerStatus(res.status)) {
          return;
        }

        // Connect SSE once we have a generationUuid, if not already connected/given-up.
        if (res.generationUuid) {
          if (isTerminalCallRefinementServerStatus(res.status)) {
            console.log(`[refinement-sse] skipping SSE connect — status "${res.status}" is terminal`);
          } else if (this.sseSubscriptions.has(callUuid)) {
            console.log(`[refinement-sse] skipping SSE connect — already subscribed for callUuid=${callUuid}`);
          } else if (this.sseGivenUp.has(callUuid)) {
            console.log(`[refinement-sse] skipping SSE connect — previously gave up for callUuid=${callUuid}`);
          } else {
            this.connectSse(callUuid, res.generationUuid);
          }
        }

        const statusChanged = res.status !== record.serverStatus;
        this.upsertRecord({
          ...record,
          serverStatus: res.status,
          ...(res.generationUuid ? { generationUuid: res.generationUuid } : {}),
          statusChangedAt: statusChanged ? Date.now() : record.statusChangedAt,
        });
        this.scheduleNextOrStop(callUuid, res.status);
      },
      error: () => {
        console.warn(`[refinement-sse] poll error for callUuid=${callUuid} — retrying on next interval`);
        // Transient/network failure — retry on schedule rather than flipping
        // to Error from a transport blip; don't touch the stored status.
        this.scheduleNextOrStop(callUuid, this.records.get(callUuid)?.serverStatus);
      },
    });
  }

  /** Shared update path for SSE events — applies status + optional reason
   * through the same state mutation and scheduling as a poll response. */
  private applyStatusUpdate(callUuid: string, status: string, reason?: string): void {
    const record = this.records.get(callUuid);
    if (!record) return;

    // If we are past the polling cap, replayed SSE events must not overwrite
    // the stored status with a stale value. Disconnect and fire a fresh poll.
    if (Date.now() - record.startedAt >= POLLING_CAP_MS) {
      console.log(`[refinement-sse] past cap — ignoring replayed event "${status}" for callUuid=${callUuid}, falling back to poll`);
      this.disconnectSse(callUuid);
      this.pollOne(callUuid);
      return;
    }

    console.log(
      `[refinement-sse] event → callUuid=${callUuid} status=${status}` +
      (reason ? ` reason=${reason}` : ''),
    );

    const statusChanged = status !== record.serverStatus;
    this.upsertRecord({
      ...record,
      serverStatus: status,
      ...(reason !== undefined ? { reason } : {}),
      statusChangedAt: statusChanged ? Date.now() : record.statusChangedAt,
    });
    this.scheduleNextOrStop(callUuid, status);
  }

  private connectSse(callUuid: string, generationUuid: string): void {
    console.log(`[refinement-sse] connecting — callUuid=${callUuid} generationUuid=${generationUuid}`);
    this.sseService.connect(SSE_CONTEXT_TYPE, generationUuid);

    if (!this.sseService.isConnected(SSE_CONTEXT_TYPE, generationUuid)) {
      // Client cap reached — stay on normal polling intervals for this context.
      console.warn(`[refinement-sse] cap reached — callUuid=${callUuid} will stay on ${POLL_INTERVAL_MS / 1000}s polling`);
      this.sseGivenUp.add(callUuid);
      return;
    }

    console.log(`[refinement-sse] connected — subscribing to generation_status_changed for callUuid=${callUuid}`);

    const eventSub = this.sseService
      .on<{ status: string; reason?: string }>(SSE_CONTEXT_TYPE, generationUuid, 'generation_status_changed')
      .subscribe(({ status, reason }) => this.applyStatusUpdate(callUuid, status, reason));

    const closedSub = this.sseService.closed$(SSE_CONTEXT_TYPE, generationUuid).subscribe(() => {
      // Stream permanently died — unsubscribe, mark as given up, and resume
      // fast (5 s) polling immediately by cancelling any pending safety timer.
      console.warn(`[refinement-sse] stream closed — callUuid=${callUuid} resuming ${POLL_INTERVAL_MS / 1000}s polling immediately`);
      const sub = this.sseSubscriptions.get(callUuid);
      if (sub) {
        sub.unsubscribe();
        this.sseSubscriptions.delete(callUuid);
      }
      this.sseGivenUp.add(callUuid);
      this.sseGapPollNeeded.delete(callUuid);
      const handle = this.activeTimers.get(callUuid);
      if (handle !== undefined) clearTimeout(handle);
      this.activeTimers.delete(callUuid);
      this.startPollingFor(callUuid);
    });

    const sub = new Subscription();
    sub.add(eventSub);
    sub.add(closedSub);
    this.sseSubscriptions.set(callUuid, sub);

    // No gap poll needed for refinement — all intermediate statuses render identically
    // ("Thinking"), so a missed transition in the snapshot→stream window has no visible
    // effect. Terminal statuses are caught by the 60 s safety poll.
    console.log(`[refinement-sse] subscribed — safety polls every ${SSE_SAFETY_INTERVAL_MS / 1000}s while stream is live`);
  }

  private disconnectSse(callUuid: string): void {
    const sub = this.sseSubscriptions.get(callUuid);
    if (sub) {
      sub.unsubscribe();
      this.sseSubscriptions.delete(callUuid);
      console.log(`[refinement-sse] unsubscribed — callUuid=${callUuid}`);
    }
    const generationUuid = this.records.get(callUuid)?.generationUuid;
    if (generationUuid) {
      this.sseService.disconnect(SSE_CONTEXT_TYPE, generationUuid);
    }
  }

  private scheduleNextOrStop(callUuid: string, latestServerStatus: string | undefined): void {
    const record = this.records.get(callUuid);
    if (!record || !latestServerStatus || isTerminalCallRefinementServerStatus(latestServerStatus)) {
      const handle = this.activeTimers.get(callUuid);
      if (handle !== undefined) clearTimeout(handle);
      this.activeTimers.delete(callUuid);
      this.disconnectSse(callUuid);
      console.log(`[refinement-sse] stopped — callUuid=${callUuid} reached terminal status "${latestServerStatus}"`);
      return;
    }

    if (Date.now() - record.startedAt >= POLLING_CAP_MS) {
      console.log(`[refinement-sse] polling cap reached — callUuid=${callUuid} last status="${latestServerStatus}"`);
      const handle = this.activeTimers.get(callUuid);
      if (handle !== undefined) clearTimeout(handle);
      this.activeTimers.delete(callUuid);
      this.disconnectSse(callUuid);
      this.upsertRecord({
        ...record,
        serverStatus: CallRefinementServerStatus.Failed,
        pollingCapReached: true,
      });
      return;
    }

    const generationUuid = record.generationUuid;
    const sseConnected = !!generationUuid && this.sseService.isConnected(SSE_CONTEXT_TYPE, generationUuid);

    let nextInterval: number;
    let mode: string;
    if (sseConnected) {
      if (this.sseGapPollNeeded.has(callUuid)) {
        this.sseGapPollNeeded.delete(callUuid);
        nextInterval = 0;
        mode = 'SSE gap-poll (immediate)';
      } else {
        nextInterval = SSE_SAFETY_INTERVAL_MS;
        mode = `SSE safety (${SSE_SAFETY_INTERVAL_MS / 1000}s)`;
      }
    } else {
      const staleFor = Date.now() - record.statusChangedAt;
      const isStale = staleFor >= STALE_THRESHOLD_MS;
      nextInterval = isStale ? STALE_BACKOFF_INTERVAL_MS : POLL_INTERVAL_MS;
      mode = isStale
        ? `polling stale-backoff (${STALE_BACKOFF_INTERVAL_MS / 1000}s)`
        : `polling (${POLL_INTERVAL_MS / 1000}s)`;
    }

    console.log(`[refinement-sse] next poll in ${nextInterval}ms — mode: ${mode} — callUuid=${callUuid}`);

    const existing = this.activeTimers.get(callUuid);
    if (existing !== undefined) clearTimeout(existing);
    const handle = setTimeout(() => this.pollOne(callUuid), nextInterval);
    this.activeTimers.set(callUuid, handle);
  }
}
